import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null=loading, false=guest, obj=authed
    const [error, setError] = useState(null);

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
            // Directive 3+4 · Language auto-detect handshake. Runs once per
            // authenticated user — if the backend has no `user_locale` yet,
            // sniff the browser's Accept-Language and persist it so
            // returning visitors never see the wrong tongue.
            //
            // Fire-and-forget, deliberately NOT awaited and deliberately
            // NOT followed by a second `setUser()`. The previous version
            // awaited the POST, re-fetched `/auth/me`, and called `setUser`
            // a second time — every consumer with `useEffect(..., [user])`
            // (both consent modals) saw two back-to-back "user changed"
            // renders on every new signup, which is what caused the
            // flicker. The locale write is best-effort UI polish; it
            // doesn't need to block or re-hydrate the session.
            if (data && !data.user_locale && !data.preferred_language) {
                const nav = (navigator.language || navigator.userLanguage || "en").split("-")[0].toUpperCase();
                const MAP = { EN: "EN", SW: "SW", FR: "FR", AR: "AR", PT: "PT" };
                const locale = MAP[nav] || "EN";
                api.post("/beco/locale", { locale }).catch(() => { /* non-blocking */ });
            }
            return data;
        } catch {
            setUser(false);
            return null;
        }
    }, []);

    useEffect(() => { refresh(); }, [refresh]);

    const login = useCallback(async (email, password, recaptchaToken = "") => {
        setError(null);
        try {
            await api.post("/auth/login", { email, password }, {
                headers: recaptchaToken ? { "X-Recaptcha-Token": recaptchaToken } : {},
            });
            // Auth token lives ONLY in the httpOnly cookie set by the backend.
            // We deliberately ignore `data.access_token` in the response body.
            await refresh();
            return true;
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
            return false;
        }
    }, [refresh]);

    const register = useCallback(async (payload, recaptchaToken = "") => {
        setError(null);
        try {
            const { data } = await api.post("/auth/register", payload, {
                headers: recaptchaToken ? { "X-Recaptcha-Token": recaptchaToken } : {},
            });
            return data;
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
            return null;
        }
    }, []);

    const logout = useCallback(async () => {
        try { await api.post("/auth/session/logout"); } catch { /* ignore */ }
        try { await api.post("/auth/logout"); } catch { /* ignore */ }
        // Clear any legacy token that may still linger from a prior release.
        try { localStorage.removeItem("mlima_token"); } catch { /* private mode */ }
        setUser(false);
    }, []);

    // First-party Google Auth. The frontend hits our own /api/auth/google/start,
    // gets Google's consent URL, and does a top-level redirect. Google then
    // redirects back to /api/auth/google/callback where the backend exchanges
    // the code, verifies the ID token, sets the session_token cookie, and
    // 303-redirects to /auth/callback (new signup) or /app (returning).
    const beginGoogle = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/google/start");
            if (data?.url && data.url.startsWith("https://accounts.google.com/")) {
                window.location.assign(data.url);
            } else {
                setError("Unable to start Google sign-in.");
            }
        } catch (e) {
            setError(formatApiErrorDetail(e) || "Unable to start Google sign-in.");
        }
    }, []);

    // Stable value reference — every downstream `useAuth()` consumer would
    // otherwise re-render on unrelated parent renders.
    const value = useMemo(
        () => ({ user, error, setError, login, register, logout, refresh, setUser, beginGoogle }),
        [user, error, login, register, logout, refresh, beginGoogle],
    );

    return (
        <AuthCtx.Provider value={value}>
            {children}
        </AuthCtx.Provider>
    );
}
